import {
  Button,
  CustomIconName,
  DataGrid,
  Icon,
  Status,
  StatusState,
  StatusVariant,
  TableCell,
  TextInput,
  theme,
} from '@ketch-com/deck'
import { PermitHistoryDTO, PermitHistoryPermitPropertiesItemDTO } from '@ketch-com/figurehead'
import { Box, Tooltip, Typography } from '@mui/material'
import { HistoricalPermitDatePicker } from 'pages/insights/auditLog/components/historicalPermitPopup/components/HistoricalPermitDatePicker'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { ReactNode, useMemo, useState } from 'react'
import { LegalBasis, RequiresOptInLegalBases } from 'utils/constants/legalBases'
import { useDebounce } from 'react-use'
import { ConsentSource } from '@ketch-sdk/ketch-types'
import { SourceLabels } from 'pages/insights/auditLog/constants'
import { EmptyValueRenderer } from 'components/EmptyValueRenderer'
import { useAuditLogContext } from 'pages/insights/auditLog/context/AuditLogContext'
import { NoDataEmptyState } from 'pages/insights/auditLog/components/NoDataEmptyState'

export type HistoricalConsentContentProps = {
  permitHistory: PermitHistoryDTO[]
}

const columns: GridColDef<PermitHistoryPermitPropertiesItemDTO>[] = [
  {
    align: 'left',
    field: 'consentState',
    width: 175,
    headerAlign: 'left',
    headerName: 'Consent State',
    renderHeader: params => <Typography variant="smallLabel">{params.colDef.headerName}</Typography>,
    sortable: false,
    renderCell: ({ row: permitProperty }: GridRenderCellParams<PermitHistoryPermitPropertiesItemDTO>) => {
      const allowed = !!permitProperty?.allowed

      return (
        <TableCell>
          <Status
            variant={StatusVariant.filled}
            status={allowed ? StatusState.success : StatusState.error}
            label={allowed ? 'Opted In' : 'Opted Out'}
            icon={allowed ? 'OCheck' : 'OCross'}
          />
        </TableCell>
      )
    },
  },
  {
    align: 'left',
    field: 'defaultState',
    width: 175,
    headerAlign: 'left',
    headerName: 'Default',
    renderHeader: params => <Typography variant="smallLabel">{params.colDef.headerName}</Typography>,
    sortable: false,
    renderCell: ({ row: permitProperty }: GridRenderCellParams<PermitHistoryPermitPropertiesItemDTO>) => {
      const defaultState = !RequiresOptInLegalBases.includes((permitProperty?.legalBasisCode as LegalBasis) ?? '')

      return (
        <TableCell>
          <Status
            variant={StatusVariant.filled}
            status={defaultState ? StatusState.success : StatusState.error}
            label={defaultState ? 'Opted In' : 'Opted Out'}
            icon={defaultState ? 'OCheck' : 'OCross'}
          />
        </TableCell>
      )
    },
  },
  {
    align: 'left',
    field: 'purposeCode',
    flex: 2,
    headerAlign: 'left',
    headerName: 'Purpose',
    renderHeader: params => <Typography variant="smallLabel">{params.colDef.headerName}</Typography>,
    sortable: false,
    renderCell: ({ row: permitProperty }: GridRenderCellParams<PermitHistoryPermitPropertiesItemDTO>) => {
      return (
        <TableCell>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start' }}
          >
            <Typography variant="label">{permitProperty?.purposeCode}</Typography>
            <Typography variant="smallBody">{permitProperty?.purposeCode}</Typography>
          </Box>
        </TableCell>
      )
    },
  },
]

type CardProps = {
  title: string
  value: string | ReactNode
  icon: CustomIconName
}

export const HistoricalConsentContent: React.FC<HistoricalConsentContentProps> = ({ permitHistory }) => {
  const { setShowViewExperienceModal, setAuditLogExperiencePreviewPermit } = useAuditLogContext()

  // Purpose search
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Filter the permit history for permits that contain a purpose matching the search
  const permitHistoryOptions = useMemo(() => {
    return permitHistory
      .filter(item => {
        const search = debouncedSearch.toLowerCase()
        return (
          !search ||
          item?.permitProperties?.some(property => {
            return (
              property?.purposeName?.toLowerCase().includes(search) ||
              property?.purposeCode?.toLowerCase().includes(search)
            )
          })
        )
      })
      .sort((a, b) => (b.collectedAt ?? 0) - (a.collectedAt ?? 0))
  }, [permitHistory, debouncedSearch])

  const [selectedPermit, setSelectedPermit] = useState<PermitHistoryDTO | null>(permitHistoryOptions?.[0] || null)
  const disableViewExperienceButton = !selectedPermit?.configID
  const noHistory = !permitHistoryOptions.length

  // Filter the purpose table for rows matching search
  const purposeTableRows = useMemo(() => {
    return (selectedPermit?.permitProperties || []).filter(property => {
      const search = debouncedSearch.toLowerCase()
      return (
        !search ||
        property?.purposeName?.toLowerCase().includes(search) ||
        property?.purposeCode?.toLowerCase().includes(search)
      )
    })
  }, [selectedPermit, debouncedSearch])
  const rowCount = purposeTableRows.length
  const showPagination = rowCount > 5

  useDebounce(
    () => {
      setDebouncedSearch(search)
    },
    500,
    [search],
  )

  // Summary card data
  const cards: CardProps[] = useMemo(() => {
    return [
      {
        title: 'Jurisdiction',
        value: selectedPermit?.jurisdictionName || selectedPermit?.jurisdiction || <EmptyValueRenderer />,
        icon: 'FScales',
      },
      {
        title: 'Property',
        value: selectedPermit?.propertyName || selectedPermit?.propertyCode || <EmptyValueRenderer />,
        icon: 'OGlobe',
      },
      {
        title: 'Interaction Method',
        value: SourceLabels[selectedPermit?.source as ConsentSource] || selectedPermit?.source || 'Unknown',
        icon: 'OPointer',
      },
      {
        title: 'GPC Signal',
        value: selectedPermit?.permitProperties?.[0]?.isGpcEnabled ? 'Yes' : 'No',
        icon: 'OBroadcast',
      },
    ]
  }, [selectedPermit])

  return noHistory ? (
    <NoDataEmptyState
      title="No Permit History"
      subtitle="There is no permit history for this identifier."
      iconName="OClock"
    />
  ) : (
    <Box display="flex" flexDirection="column" gap="32px">
      {/* Date picker, purpose search, and preview button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" width="912px">
        <Box display="flex" gap="14px">
          <HistoricalPermitDatePicker
            options={permitHistoryOptions}
            value={selectedPermit}
            onChange={permit => setSelectedPermit(permit)}
          />
          <TextInput
            value={search}
            onChange={e => {
              setSearch(e.target.value)
            }}
            placeholder="Search for a purpose"
            startIcon={<Icon name={'OMag'} iconColor={theme.palette.Black.o48} />}
            size="small"
          />
        </Box>
        <Tooltip
          title={
            disableViewExperienceButton
              ? 'You are unable to view the experience associated with this consent record. This is either because the consent was set non-interactively, or it was set before consent records were associated with experiences.'
              : ''
          }
          placement="bottom"
        >
          <span>
            <Button
              color="secondary"
              onClick={() => {
                setAuditLogExperiencePreviewPermit(selectedPermit || null)
                setShowViewExperienceModal(true)
              }}
              disabled={disableViewExperienceButton}
            >
              View Experience
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Summary Cards */}
      <Box display="flex" gap="8px">
        {cards.map(card => (
          <Box
            key={card.title}
            display="flex"
            padding="8px 16px"
            gap="24px"
            borderLeft={`1px solid ${theme.palette.Common.Divider}`}
          >
            <Box display="flex" flexDirection="column">
              <Typography variant="smallBody" color={theme.palette.Text.Secondary}>
                {card.title}
              </Typography>
              <Typography variant="smallLabel">{card.value}</Typography>
            </Box>
            <Icon name={card.icon} width={20} height={20} iconColor={theme.palette.Text.Secondary} />
          </Box>
        ))}
      </Box>

      {/* Purposes Table */}
      <DataGrid
        autosizeOnMount
        autosizeOptions={{
          expand: true,
          includeHeaders: true,
          includeOutliers: false,
        }}
        getRowHeight={() => 'auto'}
        getRowId={(row: PermitHistoryPermitPropertiesItemDTO) => row.purposeCode ?? ''}
        columns={columns}
        rows={purposeTableRows}
        checkboxSelection={false}
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnPinning
        disableColumnReorder
        disableColumnResize
        disableChildrenSorting={false}
        disableRowHoverStates
        paginationMode="client"
        rowCount={rowCount}
        pagination={showPagination}
        hideFooter={!showPagination}
        pageSizeOptions={[5, 10, 20]}
        noRowsOverlayProps={{ buttonTitle: '' }}
        sx={{
          '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox):hover': {
            backgroundImage: 'unset',
            cursor: 'auto',
          },
        }}
      />
    </Box>
  )
}
